import { useEffect, useState } from 'react'

import { useDocumentFocus } from '@core/hooks/useDocumentFocus'
import { useLocalStorage } from '@core/hooks/useLocalStorage'
import { useTabId } from '@core/hooks/useTabId'

/**
 * @returns Whether this was the most recently used tab for the current domain.
 *
 * This is different than simply checking document.hasFocus() or listening to a
 * focus/blur event, because it allows us to track which was the most "recent"
 * tab to have focus.
 */
export function useIsFocusedTab() {
  const isDocumentFocused = useDocumentFocus()
  const tabId = useTabId()
  const [lastFocusedTab, setLastFocusedTab] = useLocalStorage('lastFocusedTab')
  const [isFocusedTab, setIsFocusedTab] = useState(
    isDocumentFocused ||
      // When the tab is not focused, but it was the last focused tab, we should
      // consider this tab as focused on pageload. This is meant to fix a
      // scenario in One-to-One where if a visitors' browser is not focused, and
      // they are pushed to a new url by a sales associate, the video call would
      // drop unless the visitor clicks to focus the document within a timeout.
      tabId === lastFocusedTab,
  )

  useEffect(() => {
    if (lastFocusedTab !== tabId) {
      setIsFocusedTab(false)
    }
  }, [lastFocusedTab, tabId])

  // On the initial page load, or when the tab regains focus, updates focused
  // state and notifies other tabs that this one has been focused.
  useEffect(() => {
    if (isDocumentFocused) {
      setLastFocusedTab(tabId)
      setIsFocusedTab(true)
    }
  }, [isDocumentFocused, setLastFocusedTab, tabId])

  return isFocusedTab
}
